import axios from 'axios';

import { IHairAdvisorData } from '@notino/hair-analyzer-lib';

import { NewRelicBrowser } from '@lib/newRelic/NewRelicBrowser';

interface IApiClientFactory {
  addToCart: (productId: string) => Promise<void>;
  addToWishlist: (productId: string) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  /** Consumed by useDatabaseTracking from @notino/hair-analyzer-lib. */
  saveTrackingData: (
    data: IHairAdvisorData,
    walkthroughId: string,
    isRoutineComplete: boolean
  ) => Promise<void>;
}

const logFailure = (action: string, apiUri: string, error: unknown) => {
  NewRelicBrowser.agent.noticeError(
    error instanceof Error ? error : new Error(String(error)),
    { source: 'hairAnalyzer', action, request: apiUri }
  );
};

export const apiClientFactory = ({
  apiUrl,
}: {
  apiUrl: string;
}): IApiClientFactory => {
  const client = axios.create({
    baseURL: apiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    // Anything outside 2xx/3xx rejects, so failures land in the catch blocks.
    maxRedirects: 0,
    validateStatus: (statusCode) => statusCode >= 200 && statusCode < 400,
  });

  return {
    addToCart: async (productId: string) => {
      const apiUri = '/cart/add';

      try {
        await client.post(apiUri, { productId });
      } catch (error) {
        logFailure('add to cart', apiUri, error);
      }
    },
    addToWishlist: async (productId: string) => {
      const apiUri = `/wishlist/${productId}`;

      try {
        await client.post(apiUri, null);
      } catch (error) {
        logFailure('add to wishlist', apiUri, error);
      }
    },
    removeFromWishlist: async (productId: string) => {
      const apiUri = `/wishlist/${productId}`;

      try {
        await client.delete(apiUri);
      } catch (error) {
        logFailure('remove from wishlist', apiUri, error);
      }
    },
    saveTrackingData: async (
      data: IHairAdvisorData,
      walkthroughId: string,
      isRoutineComplete: boolean
    ) => {
      const apiUri = '/user/tracking';

      try {
        await client.patch(apiUri, { walkthroughId, data, isRoutineComplete });
      } catch (error) {
        logFailure('save tracking data', apiUri, error);
      }
    },
  };
};
